import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

/**
 * Passwordless vendor sign-in: invitation link + email + OTP.
 */
const VendorLoginPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get("token") || "";

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inviteOk, setInviteOk] = useState(null);

  useEffect(() => {
    if (!tokenFromUrl) {
      setInviteOk(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get("/api/vendor-auth/validate-invite", {
          params: { token: tokenFromUrl },
        });
        if (!cancelled) setInviteOk(Boolean(data.valid));
      } catch {
        if (!cancelled) setInviteOk(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tokenFromUrl]);

  const requestOtp = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !tokenFromUrl) {
      setError("Enter your email and use the link from your invitation.");
      return;
    }
    setLoading(true);
    try {
      await axios.post("/api/vendor-auth/request-otp", {
        email: email.trim().toLowerCase(),
        inviteToken: tokenFromUrl,
      });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not send code");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post("/api/vendor-auth/verify-otp", {
        email: email.trim().toLowerCase(),
        inviteToken: tokenFromUrl,
        otp: otp.trim(),
      });
      if (data.accessToken) {
        localStorage.setItem("vendorAccessToken", data.accessToken);
        navigate("/vendor/portal");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen d-flex align-items-center justify-content-center bg-light py-5">
      <div className="card shadow-sm" style={{ maxWidth: 420, width: "100%" }}>
        <div className="card-body p-4">
          <h1 className="h4 mb-3">Vendor sign-in</h1>
          {!tokenFromUrl ? (
            <p className="text-danger small">Open this page using the link from your invitation email.</p>
          ) : inviteOk === false ? (
            <p className="text-danger small">This invitation link is invalid or has expired. Ask your contact to send a new request.</p>
          ) : inviteOk === null ? (
            <p className="text-muted small">Checking invitation…</p>
          ) : (
            <>
              {step === 1 ? (
                <form onSubmit={requestOtp}>
                  <p className="small text-muted">Enter the email address that received the invitation.</p>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-success w-100" disabled={loading}>
                    {loading ? "Sending…" : "Send verification code"}
                  </button>
                </form>
              ) : (
                <form onSubmit={verifyOtp}>
                  <p className="small text-muted">Enter the code sent to your email.</p>
                  <div className="mb-3">
                    <label className="form-label">Verification code</label>
                    <input
                      className="form-control"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-success w-100 mb-2" disabled={loading}>
                    {loading ? "Signing in…" : "Sign in"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    onClick={() => {
                      setStep(1);
                      setOtp("");
                    }}
                  >
                    Use a different email
                  </button>
                </form>
              )}
            </>
          )}
          {error ? (
            <div className="alert alert-danger mt-3 mb-0 py-2 small" role="alert">
              {error}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default VendorLoginPage;
